
import { defineComponent, ref, onMounted, watch } from 'vue';
import PedidoService from '@/services/sit-pedidos/pedido.service';
import TransportadoraAuthService from '@/services/transportadora.auth.service';
import globalStore from '@/store/modules/global';
import stepDadosCooperado from './components/stepDadosCooperado.vue';
import stepDocumentacao from './components/stepDocumentacao.vue';

export default defineComponent({
  name: 'SubstituicaoCooperado',
  components: {
    stepDadosCooperado,
    stepDocumentacao,
  },
  setup() {
    const cooperados = ref<any[]>([]);
    const loading = ref(false);
    const headers = [
      { text: 'ID', value: 'id' },
      { text: 'Nome', value: 'nome' },
      { text: 'CPF', value: 'cpf' },
      { text: 'Situação', value: 'situacao' },
      // Adicione mais colunas conforme necessário
    ];

    const transportadora = ref<any>(null);

    const IdPedido = ref<number>(0);
    const sexoCooperado = ref<string>('');

    const formSubstituicao = ref({
      pedido: {
        idTransportadora: null,
        tipoPedido: 'SUBSTITUICAO_COOPERADO',
        idCooperado: null,
        // Dados do substituído — só para visualização no passo 2, NÃO vão no payload.
        placa: null as string | null,
        nomeSubstituido: null as string | null,
        cpfSubstituido: null as string | null,
        // Checagem "o veículo é da empresa?" (permite-aproveitar), preenchida ao
        // selecionar o cooperado. documentoProprietario VAI no payload (persistido);
        // veiculoTransportadoraId/permiteAproveitar são só de controle.
        veiculoTransportadoraId: null as number | null,
        permiteAproveitar: null as boolean | null,
        documentoProprietario: null as string | null,
      },
      documents: [],
    });

    const form = ref({
      currentStep: 1,
    });

    function constructorForm(val) {
      formSubstituicao.value.pedido = {
        ...formSubstituicao.value.pedido,
        ...val,
      };
    }

    function NextDocument(val) {
      form.value.currentStep = val.nextStep;
      IdPedido.value = val.idPedido;
      sexoCooperado.value = val.sexo || '';
    }

    watch(
      () => formSubstituicao.value.pedido.idCooperado,
      newVal => {
        if (newVal) {
          // Guarda dados do cooperado selecionado (só para exibir no passo 2).
          const selecionado = cooperados.value.find((c: any) => c.id === newVal);
          formSubstituicao.value.pedido.placa = selecionado?.placa || null;
          formSubstituicao.value.pedido.nomeSubstituido = selecionado?.nome || null;
          formSubstituicao.value.pedido.cpfSubstituido = selecionado?.cpf || null;
          // Dispara a checagem "o veículo é da empresa?" (1 requisição).
          checarPermiteAproveitar(selecionado);
          form.value.currentStep = 2;
        } else {
          formSubstituicao.value.pedido.placa = null;
          formSubstituicao.value.pedido.nomeSubstituido = null;
          formSubstituicao.value.pedido.cpfSubstituido = null;
          resetPermiteAproveitar();
        }
      }
    );

    // Zera a checagem de propriedade do veículo.
    function resetPermiteAproveitar() {
      formSubstituicao.value.pedido.veiculoTransportadoraId = null;
      formSubstituicao.value.pedido.permiteAproveitar = null;
      formSubstituicao.value.pedido.documentoProprietario = null;
    }

    // "O veículo é da empresa?" — 1 requisição ao selecionar o cooperado.
    //   permiteAproveitar === true  -> mostra o campo "aproveitar veículo"
    //   permiteAproveitar === false -> confirmado que NÃO é da empresa: esconde
    //   permiteAproveitar === null  -> ainda checando OU deu erro. Erro NÃO é
    //     "não pode": a gente avisa e o usuário re-seleciona pra tentar de novo.
    function checarPermiteAproveitar(selecionado: any) {
      resetPermiteAproveitar();
      const vtId = selecionado?.veiculoTransportadoraId ?? null;
      formSubstituicao.value.pedido.veiculoTransportadoraId = vtId;

      // Sem veículo vinculado -> nada a aproveitar, não consulta.
      if (!vtId || !selecionado?.placa) {
        formSubstituicao.value.pedido.permiteAproveitar = false;
        return;
      }

      PedidoService.getPermiteAproveitar(vtId).subscribe({
        next: (response: any) => {
          const data = response?.data ?? response;
          formSubstituicao.value.pedido.permiteAproveitar = data?.permiteAproveitar === true;
          formSubstituicao.value.pedido.documentoProprietario = data?.documentoProprietario ?? null;
        },
        error: () => {
          // Erro = "não deu pra confirmar", NÃO "não pode". Mantém escondido e avisa.
          formSubstituicao.value.pedido.permiteAproveitar = null;
          formSubstituicao.value.pedido.documentoProprietario = null;
          globalStore.setSnackBar({
            show: true,
            message:
              'Não foi possível confirmar o veículo. Selecione o cooperado novamente para tentar de novo.',
            type: 'error',
            timeout: 5000,
          });
        },
      });
    }

    onMounted(() => {
      transportadora.value = TransportadoraAuthService.currentTransportadoraValue;
      if (transportadora.value && transportadora.value.id) {
        formSubstituicao.value.pedido.idTransportadora = transportadora.value.id;
        loading.value = true;
        PedidoService.listCooperadosTranspotadora(transportadora.value.id).subscribe({
          next: (response: any) => {
            cooperados.value = response;
            loading.value = false;
          },
          error: () => {
            cooperados.value = [];
            loading.value = false;
          },
        });
      }
    });

    function handleNewPedido() {
      form.value.currentStep = 1;
      IdPedido.value = 0;
      formSubstituicao.value = {
        pedido: {
          idTransportadora: transportadora.value ? transportadora.value.id : null,
          tipoPedido: 'SUBSTITUICAO_COOPERADO',
          idCooperado: null,
          placa: null as string | null,
          nomeSubstituido: null as string | null,
          cpfSubstituido: null as string | null,
          veiculoTransportadoraId: null as number | null,
          permiteAproveitar: null as boolean | null,
          documentoProprietario: null as string | null,
        },
        documents: [],
      };
    }

    // Monta o rótulo do cooperado no autocomplete. Só acrescenta a placa
    // quando ela existe, evitando exibir "Placa: undefined".
    function cooperadoLabel(cooperado: any): string {
      const base = `${cooperado.id} - ${cooperado.nome} / ${cooperado.cpf}`;
      return cooperado.placa ? `${base} - Placa: ${cooperado.placa}` : base;
    }

    return {
      cooperados,
      loading,
      headers,
      formSubstituicao,
      form,
      constructorForm,
      NextDocument,
      IdPedido,
      sexoCooperado,
      handleNewPedido,
      cooperadoLabel,
    };
  },
});
